









































































import {
  defineComponent,
  computed,
  Ref,
  ref,
  ComputedRef,
  onMounted,
  watch,
  onUnmounted,
  nextTick,
  reactive,
} from '@vue/composition-api';
import axios from 'axios';
import { uuid } from 'vue-uuid';
import { orderBy } from 'lodash';

// Types
import { 
  Message, 
  PendingMessage, 
  BaseMessage, 
  MessageAuthor, 
  Channel,  
  MessageBody,
  MessageAttachment
} from '@/views/Admin/Channels/channelTypes';
import { mapResponseToMessage, adminMapResponseToChannel } from '@/views/Admin/Channels/mappingUtil';

// Store
import { Endpoints, Actions } from '@/ts/store/Constants';
import { Mutations } from '@/ts/store/modules/Channels/constants';

import PersonData from '@/ts/PersonData';
import ChannelListData from '@/ts/Channels/ChannelListData';

// mixins
import DateMethods from '@/mixins/DateMethods.vue';

// composables
import useIntersectionObserver from '@/composables/useIntersectionObserver';

//components
import ChannelMessage from '@/views/Admin/Channels/components/ChannelMessage.vue';
import DeletedMessage from '@/views/Admin/Channels/components/DeletedMessage.vue';

export default defineComponent({
  components: {
    ChannelMessage,
    DeletedMessage,
  },
  props: {
    channel: ChannelListData,
  },
  mixins: [
    DateMethods,
  ],
  setup(props, context) {
    
    const store = context.root.$store;
    // @ts-ignore
    const router = context.root.$router;

    const loadMoreRef = ref(null);

    const feedRef = ref(null);

    const fileInput: Ref = ref(null);

    const userId: ComputedRef<number> = computed(() => {
      return store.getters['User/getUserId'];
    }); 

    const refetchMessages: ComputedRef<boolean> = computed(() => {
      return store.getters['Channels/getRefetchMessages'];
    });

    const user: ComputedRef<PersonData> = computed(() => {
      return store.getters['User/getUser'];
    });

    const channelInfo: Ref<Channel> = ref(null);

    const initializedMessageList = ref(false);

    const channelMessageList: Ref<BaseMessage[]> = ref([]);

    const page: Ref<number> = ref(0);

    const isLastPage: Ref<boolean> = ref(false);

    const selectedMessage: Ref<Message | null> = ref(null);

    const sortedMessageList: ComputedRef<BaseMessage[]> = computed(() => {
        return orderBy(channelMessageList.value, [function(message) {return new Date(message.createdAt).getTime(); }, ], ['desc']); 
    });

    const message: Ref<string> = ref('');

    const showAttachmentOptions = (event) => {
        fileInput.value.click();
    };
    
    const imageAttachment = reactive({
      imageData: null,
      height: null,
      width: null,
    });

    const canSendMessage = computed(() => {
      return message.value !== '' || imageAttachment.imageData !== null; 
    });

    const isMessageHidden = (id) => {
      return store.getters['Abuse/isContentHidden']('message', id);
    };

    const isUserBlocked = (id) => {
      return store.getters['Abuse/isContentBlocked']('user', id);
    };

    const { isIntersecting } = useIntersectionObserver(loadMoreRef);

    const groupedSortedMessageObject: ComputedRef<Record<string, BaseMessage[]>> = computed(() => {
        return sortedMessageList.value.reduce((groups, message) => {
            const date: Date = new Date(message.createdAt);
            const dateDay = date.toDateString();
            if (!groups[dateDay]) {
                groups[dateDay] = [];
            }
            groups[dateDay].push(message);
            return groups;
        }, {});
    });


    const onFileSelected = (file) => {
       
        const img = new Image();

        img.onload = function() {
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          const MAX_WIDTH = 1024;
          const MAX_HEIGHT = 1024;
          let width = img.width;
          let height = img.height;

          // Add the resizing logic
          if (!(width <= MAX_WIDTH && height <= MAX_HEIGHT)) {

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else if(height > width) {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            } else {
                width = MAX_HEIGHT;
                height = MAX_HEIGHT;
            }
          }        

          canvas.width = width;
          canvas.height = height;
          context.drawImage(img, 0, 0, width, height);
          const url = canvas.toDataURL("image/jpeg");

          imageAttachment.imageData = url;
          imageAttachment.height = height;
          imageAttachment.width = width;
        };
        
        img.src = window.URL.createObjectURL(file);

        (context.refs.fileInput as HTMLInputElement).value = null;
    };

     const handleImageUpload = (event) => {
      const files = event.target.files;
      const file = files[0];
      onFileSelected(file);
    }

    const removeImageAttachment = () => {
      imageAttachment.imageData = null;
      imageAttachment.height = null;
      imageAttachment.width = null;
    };

    const onBlockUser = (userId) => {
      store.dispatch(Actions.BLOCK_CONTENT, {
          type: 'user',
          id: userId,
      }).then(() => {
        selectedMessage.value = null;
      });
    };

    const onUnblockUser = (userId) => {
        store.dispatch(Actions.UNBLOCK_CONTENT, {
          type: 'user',
          id: userId,
      });
    };

    const fetchChannel = () => {
      if (props.channel) {
        axios.get(Endpoints.Admin.Channels.CHANNEL(props.channel.id)).then((response) => {
            channelInfo.value = adminMapResponseToChannel(response.data.data);
        });
      }
    };

    const fetchMessages = async (page: number): Promise<Message[]> => {
      if (props.channel.id) {
        const response = await axios.get(Endpoints.Admin.Channels.CHANNEL_MESSAGES(props.channel.id), {
          params: {
            page,
          },
        });

        return response.data.data
          .filter((message) => message.body !== null)
          .map((message) => {
            return mapResponseToMessage(message);
          });
      } else {
          return null;
      }
    };

    const onFetchMoreMessages = async () => {
        const currentScrollHeight = feedRef.value.scrollTop;
        page.value = page.value + 1;
        const fetchedMessages = await fetchMessages(page.value);
        if (fetchedMessages.length > 0) {
            const arrayCopy = Object.assign([], channelMessageList.value);
            const concatednatedArray = arrayCopy.concat(fetchedMessages);
            channelMessageList.value = concatednatedArray;

            // Is used to await the dom updates to get the current scrollTop after messages has been added
            await nextTick();

            feedRef.value.scrollTop = currentScrollHeight;
        } else {
          isLastPage.value = true;
        }
    };

    const onSendMessage = () => {
      // @ts-ignore
      context.refs.input.focus();
      if (message.value !== '' || imageAttachment.imageData !== null) {

        const pendingUserMessage = new PendingMessage();
        pendingUserMessage.dropId = uuid.v1();
          // @ts-ignore
        pendingUserMessage.createdAt = context.root.$moment.utc().format();

        if (message.value) {
          const body = new MessageBody;
          body.content = message.value;
          pendingUserMessage.body = body;
        }

        const author = new MessageAuthor;
        author.id = user.value.id;
        author.name = user.value.name;
        author.profilePicture = user.value.hasProfileImage ? user.value.profileImage : null;

        pendingUserMessage.author = author;

        if (imageAttachment.imageData) {
          const attachment = new MessageAttachment();
          attachment.width = imageAttachment.width;
          attachment.height = imageAttachment.height;
          pendingUserMessage.attachment = attachment;
        }

        // Pushing pending message to message list
        const arrayCopy = Object.assign([], channelMessageList.value);
        arrayCopy.push(pendingUserMessage);
        channelMessageList.value = arrayCopy;

        const requestObj = new FormData();
        message.value ? requestObj.append('content', message.value) : null;
        if (imageAttachment.imageData) {
          requestObj.append('imageData', imageAttachment.imageData);
          requestObj.append('imageMetaData[width]', imageAttachment.width);
          requestObj.append('imageMetaData[height]', imageAttachment.height);
        }

        message.value = '';
        imageAttachment.imageData = null;
        imageAttachment.height = null;
        imageAttachment.width = null;

        axios
          .post(
            Endpoints.Admin.Channels.CHANNEL_MESSAGES(props.channel.id),
            requestObj,
          )
          .then((response) => {
              if (response.data) {
                  const messageListCopy = Object.assign([], channelMessageList.value);

                  const index = messageListCopy.findIndex((message) => message.dropId === pendingUserMessage.dropId);
                  const sentMessage: Message = mapResponseToMessage(response.data.data);

                  messageListCopy[index] = sentMessage;
                  channelMessageList.value = messageListCopy;

                  nextTick().then(() => {
                    feedRef.value.scroll({top: 0, behavior: 'smooth'});
                  });
              }
          });
      }
    };

    onMounted( async () => {
           fetchChannel();
           fetchMessages(0);
    });

    onUnmounted(() => {
      // @ts-ignore
      channelInfo.value = null;
      channelMessageList.value = [];
    });

    // initializes the socket connection
    watch(
      channelInfo,
      async (newValue, oldValue) => {
          page.value = 1;
          const messages = await fetchMessages(page.value);
          channelMessageList.value = messages;
          initializedMessageList.value = true;

        if ((!oldValue && newValue) || newValue.conversationId !== oldValue.conversationId) {
          // @ts-ignore
          context.root.$echo
            .private('conversation.' + channelInfo.value.conversationId)
            .listen('.conversation.new-message', (messagePayload: any) => {
                if (messagePayload && userId.value !== messagePayload.author.id && (messagePayload.body || messagePayload.attachment)) {
                    const mappedMessage: Message = mapResponseToMessage(messagePayload);
                    const arrayCopy = Object.assign([], channelMessageList.value);
                    arrayCopy.push(mappedMessage);
                    channelMessageList.value = arrayCopy;
                } 
            });
        }
      },
    );

    watch(() => props.channel, (newValue, oldValue) => {
        channelInfo.value = null;
        channelMessageList.value = [];
        if (!oldValue || (newValue && (newValue.id !== oldValue.id))) {
           fetchChannel();
        }
    });

    watch(() => refetchMessages.value, async(newValue, oldValue) => {
        if (newValue) {
          page.value = 1;
          const messages = await fetchMessages(page.value);
          channelMessageList.value = messages;
          store.commit(Mutations.SET_FETCH_MESSAGES, false);
        }
    });

    watch(isIntersecting, async (isIntersecting) => {
      if (isIntersecting && initializedMessageList.value && !isLastPage.value) {
        await onFetchMoreMessages();
      }
    });

    return {
      channelInfo,
      sortedMessageList,
      message,
      initializedMessageList,
      groupedSortedMessageObject,
      userId,
      selectedMessage,
      isMessageHidden,
      isUserBlocked,
      loadMoreRef,
      feedRef,
      imageAttachment,
      showAttachmentOptions,
      canSendMessage,
      onSendMessage,
      onBlockUser,
      onUnblockUser,
      removeImageAttachment,
      fileInput,
      handleImageUpload,
    }
  }
});

